import React from 'react';
import '../styles/ForeignObjectCommon.css';
import './AcademicForeignObject.css';

interface Education {
    id: string;
    institution: string;
    degree: string;
    duration: string;
    gpa?: string;
    concentration?: string;
    track?: string;
    designation?: string;
    coursework?: string[];
}

interface Research {
    id: string;
    institution: string;
    role: string;
    duration: string;
    lab?: string;
    advisor?: string;
    description: string[];
    focus: string[];
}

interface AcademicForeignObjectProps {
    bounds: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
    groupName: string;
    displayText: string;
    isHovered: boolean;
    isSelected: boolean;
    clipBounds?: boolean;
    leftMargin?: number;
    rightMargin?: number;
    topMargin?: number;
}

// Education data
const education: Education[] = [
    {
        id: 'masters',
        institution: 'Georgia Tech',
        degree: 'MS, Computer Science',
        duration: 'May 2027',
        concentration: 'Machine Learning - Research Track'
    },
    {
        id: 'bachelors',
        institution: 'Georgia Tech',
        degree: 'BS, Computer Science',
        duration: 'May 2026',
        gpa: 'GPA 3.8',
        designation: 'Co-op designation',
        coursework: ['Computational Machine Learning', 'High Performance Computing', 'Systems & Networks']
    }
];

// Research experience data
const research: Research[] = [
    {
        id: 'research1',
        institution: 'College of Computing',
        role: 'Student Researcher',
        duration: 'Aug 2024 - Present',
        lab: 'Efficient and Intelligent Computing Lab',
        advisor: 'Yingyan (Celine) Lin',
        description: [
            'Assist professor Yingyan (Celine) Lin in efficient Generative AI research for novel Diffusion Language Models',
            'Develop hardware software codesigned (GPU) algorithm for sparse updates (targeting ICML publication)',
            'Implement proof-of-concept, parallel refresh and denoise steps to maximize GPU utilization for small batch sizes'
        ],
        focus: ['Generative AI', 'Diffusion Models', 'GPU Programming', 'Hardware-Software Codesign', 'Machine Learning']
    }
];

const AcademicForeignObject: React.FC<AcademicForeignObjectProps> = ({ 
    bounds, 
    groupName, 
    displayText, 
    isHovered, 
    isSelected,
    clipBounds = true,
    leftMargin = 32,
    rightMargin = 0,
    topMargin = 0
}) => {

    // Calculate adjusted bounds when clipping is enabled and component is selected
    const getAdjustedBounds = () => {
        if (!isSelected || !clipBounds) {
            return bounds;
        }
        
        return {
            x: bounds.x + leftMargin,
            y: bounds.y + topMargin,
            width: bounds.width - leftMargin - rightMargin,
            height: bounds.height - topMargin
        };
    };

    const adjustedBounds = getAdjustedBounds();

    if (!isSelected) {
        return (
            <div className="foreign-object-base header-only">
                <div className="header-text">{displayText}</div>
            </div>
        );
    }

    return (
        <div 
            className="foreign-object-base expanded"
            style={clipBounds ? {
                width: adjustedBounds.width,
                height: adjustedBounds.height,
                marginLeft: leftMargin,
                marginTop: topMargin,
                marginRight: rightMargin
            } : undefined}
        >
            <div className="header-text">{displayText}</div>
            
            <div className="main-container">
                
                {/* Education Section */}
                <div className="section">
                    <div className="section-title academic-section-title">EDUCATION</div>
                    <div className="section-items">
                        {education.map((edu) => (
                            <div key={edu.id} className="card-item">
                                <div className="card-header expanded">
                                    <div className="card-main">
                                        <div className="institution-degree">
                                            <span className="primary-text">{edu.institution}</span>
                                            <span className="secondary-text">{edu.degree}</span>
                                        </div>
                                        <div className="duration">{edu.duration}</div>
                                    </div>
                                </div>
                                
                                <div className="card-details">
                                    {edu.concentration && (
                                        <div className="detail-item">
                                            <strong>Concentration:</strong> {edu.concentration}
                                        </div>
                                    )}
                                    {edu.gpa && (
                                        <div className="detail-item">
                                            <strong>GPA:</strong> {edu.gpa}
                                        </div>
                                    )}
                                    {edu.designation && (
                                        <div className="detail-item">
                                            <strong>Designation:</strong> {edu.designation}
                                        </div>
                                    )}
                                    {edu.coursework && (
                                        <div className="tag-section">
                                            <div className="tag-label">Relevant Coursework:</div>
                                            <div className="tag-container">
                                                {edu.coursework.map((course, index) => (
                                                    <span key={index} className="tag-base coursework-tag">{course}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Research Section */}
                <div className="section">
                    <div className="section-title academic-section-title">RESEARCH</div>
                    <div className="section-items">
                        {research.map((res) => (
                            <div key={res.id} className="card-item">
                                <div className="card-header expanded">
                                    <div className="card-main">
                                        <div className="institution-degree">
                                            <span className="primary-text">{res.institution}</span>
                                            <span className="secondary-text">{res.role}</span>
                                        </div>
                                        <div className="duration">{res.duration}</div>
                                    </div>
                                </div>
                                
                                <div className="card-details">
                                    {res.lab && (
                                        <div className="detail-item">
                                            <strong>Lab:</strong> {res.lab}
                                        </div>
                                    )}
                                    {res.advisor && (
                                        <div className="detail-item">
                                            <strong>Advisor:</strong> {res.advisor}
                                        </div>
                                    )}
                                    <div className="description">
                                        {res.description.map((item, index) => (
                                            <div key={index} className="description-item">
                                                • {item}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="tag-section">
                                        <div className="tag-label">Research Focus:</div>
                                        <div className="tag-container">
                                            {res.focus.map((area, index) => (
                                                <span key={index} className="tag-base focus-tag">{area}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AcademicForeignObject;